import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { initPostHog, disablePostHog } from './posthog';

let initialized = false;

function applyAnalyticsConsent(cookie) {
  const accepted =
    Array.isArray(cookie?.categories) && cookie.categories.includes('analytics');
  if (accepted) {
    // Defer PostHog past the LCP event so it doesn't block the main thread
    // during the critical render path. requestIdleCallback fires when the
    // browser is idle; the 5s timeout is a hard fallback for Safari/Firefox.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => initPostHog(), { timeout: 5000 });
    } else {
      setTimeout(initPostHog, 3000);
    }
  } else {
    disablePostHog();
  }
}

export function initCookieConsent() {
  if (initialized) return;
  initialized = true;

  try {
    const result = CookieConsent.run({
    // Fire when the user makes a decision (first consent OR later change),
    // so analytics tooling is loaded / torn down at the right moment.
    onConsent: ({ cookie }) => applyAnalyticsConsent(cookie),
    onChange: ({ cookie }) => applyAnalyticsConsent(cookie),
    // Show the modal also to crawlers / headless browsers so the legal banner is fully testable
    // and indexable. The compliance notice is public-facing.
    hideFromBots: false,
    guiOptions: {
      consentModal: {
        layout: 'box wide',
        position: 'bottom center',
        equalWeightButtons: false,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        position: 'right',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
            { name: /^ph_/ },
            { name: 'posthog' },
          ],
        },
      },
    },
    language: {
      default: 'es',
      translations: {
        es: {
          consentModal: {
            title: 'Su privacidad, nuestra prioridad',
            description:
              'Auditorialegal utiliza cookies estrictamente necesarias para el funcionamiento del sitio. ' +
              'Asimismo, solicitamos su consentimiento para instalar cookies de análisis y medición de audiencia, ' +
              'destinadas a mejorar la experiencia conforme al Art. 22 LSSI-CE y a las directrices de la AEPD.',
            acceptAllBtn: 'Aceptar todas',
            acceptNecessaryBtn: 'Rechazar todas',
            showPreferencesBtn: 'Configurar',
            footer:
              '<a href="/politica-privacidad" target="_blank" rel="noopener">Política de Privacidad</a>' +
              '<a href="/politica-cookies" target="_blank" rel="noopener">Política de Cookies</a>',
          },
          preferencesModal: {
            title: 'Centro de Preferencias de Privacidad',
            acceptAllBtn: 'Aceptar todas',
            acceptNecessaryBtn: 'Rechazar todas',
            savePreferencesBtn: 'Guardar mis preferencias',
            closeIconLabel: 'Cerrar panel',
            serviceCounterLabel: 'Servicio|Servicios',
            sections: [
              {
                title: 'Uso de cookies',
                description:
                  'Conforme al Art. 22 LSSI-CE y al RGPD (Reglamento UE 2016/679), Auditorialegal mantiene una política activa ' +
                  'de consentimiento previo y granular. A continuación puede revisar y configurar cada categoría de cookies.',
              },
              {
                title: 'Cookies estrictamente necesarias',
                description:
                  'Imprescindibles para el funcionamiento del sitio web. No requieren consentimiento conforme ' +
                  'al artículo 22.2 LSSI-CE. Permiten la navegación segura, el envío del formulario de contacto ' +
                  'y la persistencia de sus preferencias de privacidad.',
                linkedCategory: 'necessary',
                cookieTable: {
                  caption: 'Cookies técnicas',
                  headers: {
                    name: 'Nombre',
                    domain: 'Dominio',
                    desc: 'Descripción',
                  },
                  body: [
                    {
                      name: 'cc_cookie',
                      domain: 'auditorialegal.es',
                      desc: 'Almacena sus preferencias de consentimiento. Vigencia: 6 meses.',
                    },
                  ],
                },
              },
              {
                title: 'Cookies de análisis y medición',
                description:
                  'Permiten cuantificar visitantes y medir cómo se interactúa con la web (de forma agregada y anónima) ' +
                  'para mejorar el contenido y la experiencia de usuario. Solo se activarán con su consentimiento expreso ' +
                  '(Art. 22.2 LSSI-CE).',
                linkedCategory: 'analytics',
                cookieTable: {
                  caption: 'Cookies de análisis',
                  headers: {
                    name: 'Nombre',
                    domain: 'Proveedor',
                    desc: 'Descripción',
                  },
                  body: [
                    {
                      name: '_ga / _gid',
                      domain: 'Google Analytics',
                      desc: 'Métricas de audiencia y comportamiento. Activas solo si lo autoriza.',
                    },
                    {
                      name: 'ph_*',
                      domain: 'PostHog',
                      desc: 'Analítica de producto. Datos pseudoanonimizados.',
                    },
                  ],
                },
              },
              {
                title: 'Ejercicio de derechos',
                description:
                  'Puede ejercer sus derechos de acceso, rectificación, supresión, oposición, portabilidad y ' +
                  'limitación del tratamiento en cualquier momento escribiendo a ' +
                  '<a href="mailto:info@auditorialegal.es" class="cc__link">info@auditorialegal.es</a>.',
              },
            ],
          },
        },
      },
    },
  });

    if (result && typeof result.then === 'function') {
      result
        .then(() => console.log('[CookieConsent] initialized OK'))
        .catch((err) => console.error('[CookieConsent] init failed:', err));
    } else {
      console.log('[CookieConsent] run returned (sync):', result);
    }
  } catch (err) {
    console.error('[CookieConsent] threw exception:', err);
  }
}

export function showCookiePreferences() {
  try {
    CookieConsent.showPreferences();
  } catch (e) {
    console.error('[CookieConsent] showPreferences error:', e);
  }
}

export function showCookieConsentModal() {
  try {
    CookieConsent.show(true);
  } catch (e) {
    console.error('[CookieConsent] show error:', e);
  }
}
